<script setup lang="ts">
import { TickIcon, ArrowLeft2Icon } from '@cfgtech/icons';
import { CfgTypography, CfgTypographySizes, CfgButton } from '@cfgtech/ui';

export interface Step {
  key: string,
  name: string,
  route: string,
  back: boolean,
  closeUrl: string | boolean,
  overWritePreviousStep?: string
};

const props = defineProps<{
  steps: Array<Step>,
  isFinished?: boolean
}>();

defineEmits<{
  'previous:step': [stepKey: string],
  'close:url': [stepKey: string]
}>();

// composables
const router = useRouter();

const actualStepIndex = props.steps
  .findIndex((step) =>
    router.currentRoute.value.matched
      .find(
        (route) => route.path === step.route));

const actualStep = ref(props.steps[actualStepIndex]);
const prevStep = computed(() => actualStepIndex > 0 ? props.steps[actualStepIndex - 1] : false);

const scaleX = computed(() => props.isFinished ? 1 : actualStepIndex / (props.steps.length - 1) + 0.01);
</script>

<template>
  <div class="border-b-brand-200 bg-white p-3 shadow-navbar">
    <header class="container flex h-11 flex-row items-center">
      <nav v-if="actualStep.back && (Boolean(actualStep.overWritePreviousStep) || prevStep)" class="ml-3 mt-0.5 flex overflow-hidden p-1 lg:ml-20">
        <CfgButton
          active-class="text-brand"
          class="flex flex-row whitespace-nowrap border-0 bg-transparent pr-2"
          flat
          icon-placement="left"
          :stroke="false"
          variant="primary-inverted"
          @click="$emit('previous:step', actualStep.key); $router.go(-1)"
        >
          <template #icon>
            <ArrowLeft2Icon class="mb-0 text-[24px] text-brand" />
          </template>

          <CfgTypography class="text-grey-400" :size="CfgTypographySizes.md">
            {{ actualStep.overWritePreviousStep ? actualStep.overWritePreviousStep : prevStep.name }}
          </CfgTypography>
        </CfgButton>
      </nav>

      <div v-if="actualStep.closeUrl" class="ml-auto flex">
        <CfgButton
          class="flex size-[44px] items-center justify-center"
          text
          variant="primary"
          @click="$emit('close:url', actualStep.key); $router.push(`${actualStep.closeUrl}`)"
        >
          <template #icon>
            <TickIcon class="text-[24px]" />
          </template>
        </CfgButton>
      </div>
    </header>
  </div>

  <div class="h-1.5 w-full bg-brand-light">
    <div
      class="h-1.5 w-full origin-left rounded-r-full bg-brand transition-transform duration-300 ease-in-out"
      :style="{ transform: `scaleX(${scaleX})` }"
    />
  </div>
</template>
